// Table sounds (D-109): short recorded clips (CC0, BigSoundBank and Kenney, cut by design/scripts/sfx/build.py) played
// through one Web Audio context so they start at once and overlap without cutting each other off.
//
// iPhones keep Web Audio silent until the page is tapped, so the context is created and resumed on the first tap or key
// press anywhere, and the clips load then. Web Audio follows the ring/silent switch on iPhone, which is what we want
// for effects; voice messages play through a media element and are heard with the switch on silent.
//
// Settings: `sound` is the one-tap switch for everything (table menu), `gameSounds` the cards, turn, timer and DIGU,
// `reactionSounds` the hand reactions and throws, `timerTick` the tick under ten seconds. Haptics stay on with sound off.
import { haptic } from "./motion.js";
import { registerAudioContext } from "./audioUnlock.js";

export const SFX = [
  "deal", "draw", "take", "discard", "discard-2", "turn", "tick", "tick-urgent", "timebank", "digu-sting", "group-hit", "win", "round-end",
  "react-pop", "react-bravo-2", "whoosh-2",
  "t2-poop", "t2-kiss", "t2-energy", "t2-rocket", "t2-rose", "t2-coffee", "t2-gadha", "t2-bajiya",
  "t2-fishslap", "t2-egg", "t2-slipper", "t2-juicepetty", "t2-tomato", "t2-bomb", "t2-watermelon", "t2-bulb", "t2-broom",
] as const;
export type Sfx = (typeof SFX)[number];
export const sfxUrl = (name: Sfx): string => `/art/v3/sfx/${name}.mp3`;
/** Which switch a sound belongs to. */
export const sfxKind = (name: Sfx): "game" | "reaction" => (name.startsWith("t2-") || name.startsWith("react-") || name === "whoosh-2" ? "reaction" : "game");

interface SoundPrefs { sound: boolean; gameSounds: boolean; reactionSounds: boolean; timerTick: boolean }
let prefs: SoundPrefs = { sound: true, gameSounds: true, reactionSounds: true, timerTick: true };
export function configureSound(p: Partial<SoundPrefs> & { drumOnDigu?: boolean }): void {
  prefs = { ...prefs, ...pick(p) };
}
const pick = (p: Partial<SoundPrefs>): Partial<SoundPrefs> => Object.fromEntries(Object.entries(p).filter(([k, v]) => k in prefs && typeof v === "boolean")) as Partial<SoundPrefs>;
/** The master switch (`prefs.sound`): the chimes outside the table follow it alone (D-203). */
export const soundOn = (): boolean => prefs.sound;
/** Whether `name` would play with the current switches (tests, and callers that skip work). */
export function sfxAllowed(name: Sfx): boolean {
  if (!prefs.sound) return false;
  if (name === "tick" || name === "tick-urgent") return prefs.gameSounds && prefs.timerTick;
  return sfxKind(name) === "game" ? prefs.gameSounds : prefs.reactionSounds;
}

let ctx: AudioContext | null = null;
const buffers = new Map<Sfx, AudioBuffer>();
let loading: Promise<void> | null = null;

/** The one Web Audio context for every sound in the app: the table's clips and the chimes outside it (D-203). */
export function audioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try { ctx = new Ctor(); } catch { return null; }
  // The sound strip's one tap (D-108) resumes this context along with voice.
  registerAudioContext(ctx);
  return ctx;
}

/** Fetches and decodes every clip once (about 240 KB). Safe to call often. */
export function loadSounds(): Promise<void> {
  const c = audioContext();
  if (!c || loading) return loading ?? Promise.resolve();
  loading = Promise.all(SFX.map(async (name) => {
    try {
      const res = await fetch(sfxUrl(name));
      const data = await res.arrayBuffer();
      // Older Safari only has the callback form.
      const buf = await new Promise<AudioBuffer>((ok, bad) => { const p = c.decodeAudioData(data, ok, bad) as Promise<AudioBuffer> | undefined; p?.then?.(ok, bad); });
      buffers.set(name, buf);
    } catch { /* that one stays silent */ }
  })).then(() => undefined);
  return loading;
}

/** Resumes audio inside a tap (iPhone needs that) and starts loading the clips. */
export function unlockSound(): void {
  const c = audioContext();
  if (!c) return;
  if (c.state !== "running") void c.resume().catch(() => undefined);
  // A one-sample silent buffer started inside the gesture is what finally wakes older iOS.
  try { const b = c.createBuffer(1, 1, 22050); const s = c.createBufferSource(); s.buffer = b; s.connect(c.destination); s.start(0); } catch { /* fine */ }
  void loadSounds();
}
if (typeof document !== "undefined") {
  const first = () => { unlockSound(); for (const e of EVENTS) document.removeEventListener(e, first, true); };
  const EVENTS = ["pointerdown", "keydown", "touchend"] as const;
  for (const e of EVENTS) document.addEventListener(e, first, true);
}

/** Plays one clip. `gainDb` lifts or lowers it (the target of a throw hears its landing a little louder). */
export function playSfx(name: Sfx, opts: { gainDb?: number; delayMs?: number; rate?: number } = {}): void {
  if (!sfxAllowed(name)) return;
  const c = audioContext();
  if (!c) return;
  if (c.state === "suspended") void c.resume().catch(() => undefined);
  const buf = buffers.get(name);
  if (!buf) { void loadSounds(); return; }
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    // Card sounds vary a touch so a long evening doesn't hear the same clip a hundred times.
    if (opts.rate) src.playbackRate.value = opts.rate;
    else if (name === "draw" || name === "take" || name === "discard" || name === "discard-2") src.playbackRate.value = 0.95 + Math.random() * 0.1;
    const g = c.createGain();
    g.gain.value = Math.pow(10, (opts.gainDb ?? 0) / 20);
    src.connect(g).connect(c.destination);
    src.start(c.currentTime + Math.max(0, (opts.delayMs ?? 0) / 1000));
  } catch { /* no audio */ }
}

// ---- the table's moments (names kept from the synthesised set, D-080) ----------------------------------------------

/** A card comes into your hand: from the stock, or the top discard. */
export function slip(fromDiscard = false): void { playSfx(fromDiscard ? "take" : "draw"); }
/** A card lands on the pile (anyone's discard). */
export function thud(): void { playSfx(Math.random() < 0.5 ? "discard" : "discard-2"); }
/** Each second under ten on your turn; heavier with a light haptic under five. */
export function tick(heavy: boolean): void { if (heavy) haptic(10); playSfx(heavy ? "tick-urgent" : "tick"); }
/** DIGU is declared: one drum hit, or one medium haptic with game sounds off. */
/** Where the big hit lands in the DIGU sting (D-112): the reveal times "DIGU" to it. */
export const STING_HIT_MS = 1750;
/** DIGU is declared (D-112): one bodu beru rising to a boom; one medium haptic on the hit when game sounds are off. */
export function diguSting(): number {
  if (!sfxAllowed("digu-sting")) setTimeout(() => haptic(30), STING_HIT_MS);
  playSfx("digu-sting", { gainDb: 2 });
  return STING_HIT_MS;
}
/** A group of the winning hand lands (D-112): the same drum hit, a little higher each time. */
export const GROUP_HIT_RATES = [0.92, 1.06, 1.22] as const;
export function groupHit(index: number): void { playSfx("group-hit", { rate: GROUP_HIT_RATES[Math.min(index, GROUP_HIT_RATES.length - 1)] }); }
export const dealt = (): void => playSfx("deal");
export const yourTurn = (): void => playSfx("turn");
export const timeBank = (): void => playSfx("timebank");
export const won = (): void => playSfx("win");
export const roundOver = (): void => playSfx("round-end");

/** A hand reaction arriving at a seat. */
export function reactionSound(reaction: string): void { playSfx(reaction === "bravo" ? "react-bravo-2" : "react-pop"); }
/** Throw ids → their landing sound. */
export const THROW_SFX: Record<string, Sfx> = {
  poop: "t2-poop", kiss: "t2-kiss", energy: "t2-energy", rocket: "t2-rocket", rose: "t2-rose", coffee: "t2-coffee", gadha: "t2-gadha", bajiya: "t2-bajiya", fishslap: "t2-fishslap", egg: "t2-egg", slipper: "t2-slipper", juicepetty: "t2-juicepetty", tomato: "t2-tomato", bomb: "t2-bomb", watermelon: "t2-watermelon", bulb: "t2-bulb", broom: "t2-broom",
};
/** D-113: each landing sound was made for its throw (generated recordings) and carries a little run-up before its hit; it starts this much early so the hit lands on the clip's hit. */
export const SOUND_LEAD_MS: Record<string, number> = { poop: 60, kiss: 60, energy: 138, rocket: 610, rose: 141, coffee: 59, gadha: 154, bajiya: 140, fishslap: 77, egg: 212, slipper: 79, juicepetty: 391, tomato: 220, bomb: 164, watermelon: 434, bulb: 50, broom: 310 };
/**
 * D-202: throws without a clip of their own borrow the closest one (and its run-up): the Sai break throws (D-195, which
 * only whooshed until now) and the cardsclassic packs. Keyed by throw → the throw whose clip it uses.
 */
export const BORROWED_SFX: Record<string, string> = {
  saisplash: "coffee", gulhatoss: "bajiya", bondibai: "tomato", kurumba: "watermelon", mundu: "broom",
  cake: "tomato", confetti: "bulb", balloon: "juicepetty", chili: "energy", pie: "tomato", heart: "kiss", snowball: "slipper",
  flipflop: "slipper", sandbucket: "broom", starfish: "fishslap", jellyfish: "fishslap", beachball: "energy", seaweed: "tomato", squid: "egg",
  spincard: "slipper", chipstack: "bulb", rubberchicken: "kiss", dice: "bulb", teacup: "coffee", deck: "broom", joker: "gadha",
};
for (const [id, from] of Object.entries(BORROWED_SFX)) { THROW_SFX[id] = THROW_SFX[from]!; SOUND_LEAD_MS[id] = SOUND_LEAD_MS[from]!; }
/** How long after landing each drawn impact's big moment comes (D-148: most hit the moment they land). */
export const HIT_AFTER_LAND_MS: Record<string, number> = {};
/** A throw: the whoosh now, its landing sound on the hit (`landMs` plus the clip's beat); 3 dB louder when it is thrown at you. */
export function throwSound(reaction: string, landMs: number, atYou = false): void {
  playSfx("whoosh-2", { gainDb: -3 });
  const land = THROW_SFX[reaction];
  if (land) playSfx(land, { delayMs: Math.max(0, landMs + (HIT_AFTER_LAND_MS[reaction] ?? 0) - (SOUND_LEAD_MS[reaction] ?? 0)), gainDb: atYou ? 3 : 0 });
}

/** Tests. */
export function _resetSound(): void { ctx = null; buffers.clear(); loading = null; prefs = { sound: true, gameSounds: true, reactionSounds: true, timerTick: true }; }
